import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BlurBackground = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/blur-background',
			name: 'Blur Background — AI Photo Background Blur Online | Lift',
			description:
				'Blur photo background instantly with AI. No watermark. Upload photo, blur backgrounds automatically, and create professional depth of field in seconds. Try now!',
			url: 'https://liftapp.ai/blur-background',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/blur-background#product' },
				{ '@id': 'https://liftapp.ai/blur-background#howto' },
				{ '@id': 'https://liftapp.ai/blur-background#benefits' },
				{ '@id': 'https://liftapp.ai/blur-background#faq' },
				{ '@id': 'https://liftapp.ai/blur-background#why-lift' },
				{ '@id': 'https://liftapp.ai/blur-background#explore-tools' },
				{ '@id': 'https://liftapp.ai/blur-background#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/blur-background#product',
			name: 'AI Background Blur',
			description:
				'Blur photo background instantly with AI. No watermark. Upload photo, blur backgrounds automatically, and create professional depth of field in seconds.',
			url: 'https://liftapp.ai/blur-background',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/blur-background#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift blurs the background and adds professional depth automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-background#benefits',
			name: 'Who can benefit from Background Blur?',
			description:
				"Lift's AI Background Blur is useful for content creators, marketers, small business owners, casual users, and educators who want professional depth-of-field effects.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Create eye-catching Instagram posts, TikTok videos, and YouTube thumbnails with professional portrait-style blur.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Design scroll-stopping ads and social posts that direct attention to your message.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small business owners — Create professional product photos and promotional materials without hiring photographers.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Casual users — Turn everyday snapshots into polished portraits with one-click background blur.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Enhance presentation slides and educational materials with focused, distraction-free images.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Blurred background image',
				description: 'Before-and-after example of AI background blur by Lift.',
			},
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/blur-background#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Background Blur?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Background Blur is a premium online tool that instantly blurs photo backgrounds with intelligent depth control — ideal for portraits, product shots, or creating professional-looking social media content.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Background Blur work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's background blur tool. Our AI automatically detects the main subject and blurs the background in seconds — no photo editing experience needed. Download up to 20 blurred images with professional depth-of-field effects.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Background Blur uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-background#why-lift',
			name: "Why Lift's AI Background Blur stands out",
			description:
				"Lift's Background Blur combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'AI background blur',
				description:
					'Before-and-after example of AI background blur with Lift.',
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-background#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/blur-background#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Background Blur',
					item: 'https://liftapp.ai/blur-background',
				},
			],
		},
	],
};
